import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Enrollment, Schedule, Course } from '../../types';
import { useAuthFetch } from '../../hooks/useAuthFetch';
import { usePageTitle } from '../../hooks/usePageTitle';
import { formatDate, parseLocalDate } from '../../utils/dateUtils';

type SortColumn =
  | 'studentName'
  | 'studentEmail'
  | 'courseTitle'
  | 'sessionDate'
  | 'enrollmentDate'
  | 'discountCodeUsed'
  | 'status';

type SortDirection = 'asc' | 'desc';

type ColumnFilters = {
  studentName: string;
  studentEmail: string;
  courseTitle: string;
  sessionDate: string;
  enrollmentDate: string;
  discountCodeUsed: string;
  status: string;
};

const EMPTY_FILTERS: ColumnFilters = {
  studentName: '',
  studentEmail: '',
  courseTitle: '',
  sessionDate: '',
  enrollmentDate: '',
  discountCodeUsed: '',
  status: 'all',
};

const ManageEnrollments: React.FC = () => {
  usePageTitle('Manage Enrollments');
  const [enrollments, setEnrollments] = useState<Enrollment[]>([]);
  const [schedules, setSchedules] = useState<Record<string, Schedule>>({});
  const [courses, setCourses] = useState<Record<string, Course>>({});
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState<ColumnFilters>(EMPTY_FILTERS);
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const authFetch = useAuthFetch();

  const fetchData = useCallback(async () => {
    try {
      const [enrollRes, schedRes, courseRes] = await Promise.all([
        authFetch('/api/enrollments'),
        authFetch('/api/schedules'),
        authFetch('/api/courses'),
      ]);
      if (enrollRes.ok) setEnrollments(await enrollRes.json());
      if (schedRes.ok) {
        const list: Schedule[] = await schedRes.json();
        const map: Record<string, Schedule> = {};
        list.forEach((s) => (map[s.id] = s));
        setSchedules(map);
      }
      if (courseRes.ok) {
        const list: Course[] = await courseRes.json();
        const map: Record<string, Course> = {};
        list.forEach((c) => (map[c.id] = c));
        setCourses(map);
      }
    } catch (err) {
      console.error('Error fetching enrollments:', err);
    } finally {
      setLoading(false);
    }
  }, [authFetch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateStatus = async (id: string, status: Enrollment['status']) => {
    try {
      const res = await authFetch(`/api/enrollments/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
      if (res.ok) fetchData();
    } catch (err) {
      console.error('Error updating enrollment:', err);
    }
  };

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else {
        setSortColumn(null);
        setSortDirection('asc');
      }
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortIndicator = (column: SortColumn) => {
    if (sortColumn !== column) return '';
    return sortDirection === 'asc' ? ' ▲' : ' ▼';
  };

  const getCourseTitle = useCallback(
    (e: Enrollment): string => {
      const schedule = schedules[e.scheduleId];
      const course = schedule ? courses[schedule.courseId] : null;
      return course?.title || '';
    },
    [schedules, courses],
  );

  const getSessionDate = useCallback(
    (e: Enrollment): string => schedules[e.scheduleId]?.sessionDate || '',
    [schedules],
  );

  const filteredAndSorted = useMemo(() => {
    const matches = (value: string, query: string): boolean => {
      if (!query) return true;
      return value.toLowerCase().includes(query.toLowerCase());
    };

    const filtered = enrollments.filter((e) => {
      const courseTitle = getCourseTitle(e);
      const sessionDateRaw = getSessionDate(e);
      const sessionDateDisplay = sessionDateRaw ? formatDate(sessionDateRaw) : '';
      const enrollmentDateDisplay = formatDate(e.enrollmentDate);

      if (filters.status !== 'all' && e.status !== filters.status) return false;
      if (!matches(e.studentName, filters.studentName)) return false;
      if (!matches(e.studentEmail, filters.studentEmail)) return false;
      if (!matches(courseTitle, filters.courseTitle)) return false;
      if (!matches(sessionDateDisplay, filters.sessionDate)) return false;
      if (!matches(enrollmentDateDisplay, filters.enrollmentDate)) return false;
      if (!matches(e.discountCodeUsed || '', filters.discountCodeUsed)) return false;

      return true;
    });

    if (!sortColumn) return filtered;

    const direction = sortDirection === 'asc' ? 1 : -1;

    const valueFor = (e: Enrollment): string | number => {
      switch (sortColumn) {
        case 'studentName':
          return e.studentName.toLowerCase();
        case 'studentEmail':
          return e.studentEmail.toLowerCase();
        case 'courseTitle':
          return getCourseTitle(e).toLowerCase();
        case 'sessionDate': {
          const raw = getSessionDate(e);
          return raw ? parseLocalDate(raw).getTime() : Number.POSITIVE_INFINITY;
        }
        case 'enrollmentDate':
          return new Date(e.enrollmentDate).getTime();
        case 'discountCodeUsed':
          return (e.discountCodeUsed || '').toLowerCase();
        case 'status':
          return e.status;
      }
    };

    return [...filtered].sort((a, b) => {
      const va = valueFor(a);
      const vb = valueFor(b);
      if (va < vb) return -1 * direction;
      if (va > vb) return 1 * direction;
      return 0;
    });
  }, [enrollments, filters, sortColumn, sortDirection, getCourseTitle, getSessionDate]);

  const updateFilter = (key: keyof ColumnFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner" />
        <p>Loading enrollments...</p>
      </div>
    );
  }

  return (
    <div className="admin-manage-page">
      <section className="dashboard-header">
        <div className="section-container">
          <h1 className="page-title">Manage Enrollments</h1>
        </div>
      </section>

      <section className="section">
        <div className="section-container">
          <p className="filter-results">
            {filteredAndSorted.length} enrollment{filteredAndSorted.length !== 1 ? 's' : ''}
          </p>

          <table className="admin-table">
            <thead>
              <tr>
                <th className="sortable-th" onClick={() => handleSort('studentName')}>
                  Student<span className="sort-indicator">{sortIndicator('studentName')}</span>
                </th>
                <th className="sortable-th" onClick={() => handleSort('studentEmail')}>
                  Email<span className="sort-indicator">{sortIndicator('studentEmail')}</span>
                </th>
                <th className="sortable-th" onClick={() => handleSort('courseTitle')}>
                  Course<span className="sort-indicator">{sortIndicator('courseTitle')}</span>
                </th>
                <th className="sortable-th" onClick={() => handleSort('sessionDate')}>
                  Session Date<span className="sort-indicator">{sortIndicator('sessionDate')}</span>
                </th>
                <th className="sortable-th" onClick={() => handleSort('enrollmentDate')}>
                  Enrolled<span className="sort-indicator">{sortIndicator('enrollmentDate')}</span>
                </th>
                <th className="sortable-th" onClick={() => handleSort('discountCodeUsed')}>
                  Discount<span className="sort-indicator">{sortIndicator('discountCodeUsed')}</span>
                </th>
                <th className="sortable-th" onClick={() => handleSort('status')}>
                  Status<span className="sort-indicator">{sortIndicator('status')}</span>
                </th>
                <th>Actions</th>
              </tr>
              <tr className="column-filter-row">
                <th>
                  <input
                    type="text"
                    className="column-filter-input"
                    placeholder="Filter..."
                    value={filters.studentName}
                    onChange={(e) => updateFilter('studentName', e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="column-filter-input"
                    placeholder="Filter..."
                    value={filters.studentEmail}
                    onChange={(e) => updateFilter('studentEmail', e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="column-filter-input"
                    placeholder="Filter..."
                    value={filters.courseTitle}
                    onChange={(e) => updateFilter('courseTitle', e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="column-filter-input"
                    placeholder="Filter..."
                    value={filters.sessionDate}
                    onChange={(e) => updateFilter('sessionDate', e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="column-filter-input"
                    placeholder="Filter..."
                    value={filters.enrollmentDate}
                    onChange={(e) => updateFilter('enrollmentDate', e.target.value)}
                  />
                </th>
                <th>
                  <input
                    type="text"
                    className="column-filter-input"
                    placeholder="Filter..."
                    value={filters.discountCodeUsed}
                    onChange={(e) => updateFilter('discountCodeUsed', e.target.value)}
                  />
                </th>
                <th>
                  <select
                    className="column-filter-select"
                    value={filters.status}
                    onChange={(e) => updateFilter('status', e.target.value)}
                  >
                    <option value="all">All</option>
                    <option value="active">Active</option>
                    <option value="completed">Completed</option>
                    <option value="cancelled">Cancelled</option>
                    <option value="absent">Absent</option>
                  </select>
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {filteredAndSorted.map((enrollment) => {
                const schedule = schedules[enrollment.scheduleId];
                const course = schedule ? courses[schedule.courseId] : null;
                return (
                  <tr key={enrollment.id}>
                    <td>{enrollment.studentName}</td>
                    <td>{enrollment.studentEmail}</td>
                    <td>{course?.title || 'N/A'}</td>
                    <td>{schedule ? formatDate(schedule.sessionDate) : 'N/A'}</td>
                    <td>{formatDate(enrollment.enrollmentDate)}</td>
                    <td>{enrollment.discountCodeUsed || '-'}</td>
                    <td>
                      <span className={`status-badge status-${enrollment.status}`}>
                        {enrollment.status}
                      </span>
                    </td>
                    <td className="td-actions">
                      {enrollment.status === 'active' && (
                        <>
                          <button
                            className="btn btn-text"
                            onClick={() => handleUpdateStatus(enrollment.id, 'completed')}
                          >
                            Complete
                          </button>
                          <button
                            className="btn btn-text btn-danger"
                            onClick={() => handleUpdateStatus(enrollment.id, 'cancelled')}
                          >
                            Cancel
                          </button>
                        </>
                      )}
                      {enrollment.status === 'cancelled' && (
                        <button
                          className="btn btn-text"
                          onClick={() => handleUpdateStatus(enrollment.id, 'active')}
                        >
                          Reactivate
                        </button>
                      )}
                      {enrollment.status === 'absent' && (
                        <button
                          className="btn btn-text"
                          onClick={() => handleUpdateStatus(enrollment.id, 'active')}
                        >
                          Set Active
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filteredAndSorted.length === 0 && <p className="empty-text">No enrollments found.</p>}
        </div>
      </section>
    </div>
  );
};

export default ManageEnrollments;
